<template>
  <v-card variant="outlined">
    <v-card-title class="d-flex align-center ga-3">
      <span class="text-h6" style="font-weight: 500;">操作记录</span>
      <v-spacer />
      <v-select
        v-if="isSuperAdmin"
        v-model="selectedTenantId"
        :items="tenantOptions"
        :loading="tenantLoading"
        item-title="title"
        item-value="value"
        label="选择租户"
        variant="outlined"
        density="compact"
        hide-details
        clearable
        style="max-width: 220px;"
      />
    </v-card-title>
    <v-divider />
    <v-card-text>
      <!-- 工具栏 -->
      <div class="d-flex gap-2 mb-4 align-center">
        <v-btn
          color="secondary"
          variant="outlined"
          @click="showAdvancedSearch = !showAdvancedSearch"
        >
          <v-icon left>{{ showAdvancedSearch ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>条件搜索
        </v-btn>
        <!-- 搜索 -->
        <v-btn
          color="primary"
          variant="outlined"
          @click="searchRecords"
        >
          <v-icon left>mdi-magnify</v-icon>搜索
        </v-btn>
        <v-spacer />
        <!-- 导入 -->
        <v-btn
          color="teal"
          variant="outlined"
          :loading="isImporting"
          @click="triggerImport"
        >
          <v-icon left>mdi-upload</v-icon>导入
        </v-btn>
        <!-- 导出 -->
        <v-menu>
          <template v-slot:activator="{ props }">
            <v-btn
              color="indigo"
              variant="outlined"
              v-bind="props"
              :loading="isExporting"
            >
              <v-icon left>mdi-download</v-icon>导出
              <v-icon right size="small">mdi-chevron-down</v-icon>
            </v-btn>
          </template>
          <v-list density="compact">
            <v-list-item @click="exportRecords('selected')" :disabled="selectedIds.length === 0">
              <v-list-item-title>
                <v-icon left size="small">mdi-checkbox-marked-outline</v-icon>
                导出选中 ({{ selectedIds.length }})
              </v-list-item-title>
            </v-list-item>
            <v-list-item @click="exportRecords('all')">
              <v-list-item-title>
                <v-icon left size="small">mdi-select-all</v-icon>
                导出全部
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <!-- 组合动作 -->
        <v-btn
          color="deep-purple"
          variant="outlined"
          :disabled="selectedIds.length < 2"
          :loading="comboLoading"
          @click="openStandardComboDialog"
        >
          <v-icon left>mdi-playlist-play</v-icon>组合动作 ({{ selectedIds.length }})
        </v-btn>
        <!-- 批量删除 -->
        <v-btn
          color="red"
          variant="outlined"
          :disabled="selectedIds.length === 0"
          @click="batchDeleteRecords(selectedIds)"
        >
          <v-icon left>mdi-delete-sweep</v-icon>批量删除 ({{ selectedIds.length }})
        </v-btn>
        <!-- 隐藏的文件选择器 -->
        <input
          ref="importFileInput"
          type="file"
          accept="application/json,.json"
          style="display: none;"
          @change="handleImportFileChange"
        />
      </div>
      <!-- 高级搜索面板 -->
      <v-expand-transition>
        <div v-show="showAdvancedSearch" class="mb-3">
          <v-row dense class="align-center">
            <v-col cols="6" sm="4" md="2">
              <v-text-field v-model="advancedSearch.operationName" label="操作名称" variant="outlined" density="compact" placeholder="操作名称" clearable hide-details />
            </v-col>
            <v-col cols="6" sm="4" md="2">
              <v-text-field v-model="advancedSearch.emoji" label="表情" variant="outlined" density="compact" placeholder="表情" clearable hide-details />
            </v-col>
            <v-col cols="6" sm="4" md="2">
              <v-text-field v-model="advancedSearch.version" label="版本号" variant="outlined" density="compact" placeholder="版本号" clearable hide-details />
            </v-col>
            <v-col cols="6" sm="4" md="2">
              <v-text-field v-model="advancedSearch.servoVersion" label="设备型号" variant="outlined" density="compact" placeholder="设备型号" clearable hide-details />
            </v-col>
            <v-col cols="6" sm="4" md="2">
              <v-select v-model="advancedSearch.isFavorited" label="是否收藏" variant="outlined" density="compact" :items="favoriteOptions" clearable hide-details />
            </v-col>
            <v-col cols="6" sm="4" md="2" class="d-flex justify-end align-center">
              <v-btn color="grey" variant="outlined" @click="handleResetSearch" size="large">
                <v-icon left>mdi-refresh</v-icon>重置
              </v-btn>
            </v-col>
          </v-row>
        </div>
      </v-expand-transition>
      <v-progress-linear v-if="isLoading" indeterminate class="mb-4"></v-progress-linear>
      <v-alert v-else-if="error" type="error" class="mb-4">
        数据加载失败: {{ error }}
      </v-alert>
      <v-data-table
        v-else
        v-model="selectedIds"
        show-select
        item-value="id"
        :headers="headers"
        :items="operationRecords"
        :items-per-page="itemsPerPage"
        class="elevation-0"
        hide-default-footer
        density="comfortable"
        style="border-radius: 8px;"
      >
        <template v-slot:item.params="{ item }">
          <v-sheet color="lighten-4" rounded class="pa-2" style="max-height: 100px; max-width: 280px; overflow:auto;">
            <pre class="text-xs" style="margin:0;">{{ JSON.stringify(item.params, null, 2) }}</pre>
          </v-sheet>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn icon size="small" :color="item.isFavorited ? 'yellow' : 'lighten-2'" variant="outlined" density="comfortable" class="mx-1" @click="toggleFavorite(item.id)">
            <v-icon>{{ item.isFavorited ? 'mdi-star' : 'mdi-star-outline' }}</v-icon>
          </v-btn>
          <v-btn icon size="small" color="blue lighten-1" variant="outlined" density="comfortable" class="mx-1" @click="restoreConfig(item)">
            <v-icon>mdi-undo</v-icon>
          </v-btn>
          <v-btn icon size="small" color="red lighten-1" variant="outlined" density="comfortable" class="mx-1" @click="deleteRecord(item.id)">
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </template>
        <!-- 操作名称点击查看详情 -->
        <template v-slot:item.operationName="{ item }">
          <v-btn
            text
            class="operation-name-btn"
            @click="fetchOperationDetail(item.id)"
          >
            {{ item.operationName }}
          </v-btn>
        </template>
        <template v-slot:bottom>
          <div class="d-flex justify-center align-center ga-4 mt-4">
            <v-select
              v-model="itemsPerPage"
              :items="itemsPerPageOptions"
              label="每页条数"
              variant="outlined"
              density="compact"
              hide-details
              style="max-width: 120px;"
            />
            <v-pagination
              v-model="page"
              :length="pageCount"
              :total-visible="10"
              show-first-last-page
              first-icon="mdi-chevron-double-left"
              last-icon="mdi-chevron-double-right"
              prev-icon="mdi-chevron-left"
              next-icon="mdi-chevron-right"
            ></v-pagination>
          </div>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>

  <!-- 删除确认 -->
  <v-dialog v-model="deleteDialog" max-width="400">
    <v-card>
      <v-card-title class="text-h6">确认删除</v-card-title>
      <v-card-text>确定要删除该操作记录吗？</v-card-text>
      <v-card-actions>
        <v-btn text @click="cancelDeleteRecord" variant="outlined">取消</v-btn>
        <v-btn color="red" @click="confirmDeleteRecord" variant="outlined">确认删除</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- 批量删除确认 -->
  <v-dialog v-model="batchDeleteDialog" max-width="400">
    <v-card>
      <v-card-title class="text-h6">确认批量删除</v-card-title>
      <v-card-text>确定要删除选中的 {{ batchDeleteIds.length }} 条操作记录吗？此操作不可恢复。</v-card-text>
      <v-card-actions>
        <v-btn text @click="cancelBatchDelete" variant="outlined">取消</v-btn>
        <v-btn color="red" @click="handleConfirmBatchDelete" variant="outlined">确认删除</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- 参数详情 -->
  <RecordDetailDialog
    v-model="detailDialog"
    :detail="operationDetail"
    :loading="isDetailLoading"
    :error="detailError"
  />

  <!-- 导入 -->
  <RecordImportDialog
    v-model="importDialog"
    v-model:selected-indexes="importSelectedIndexes"
    :records="importRecords"
    :importing="isImporting"
    @confirm="confirmImport"
  />

  <!-- 组合动作 -->
  <RecordComboDialog
    v-model="comboDialog"
    v-model:uniform="comboUniform"
    v-model:global-interval="comboGlobalInterval"
    :items="comboItems"
    :batch-device-count="comboExecutionOptions?.deviceIds?.length || 0"
    :loop-count="comboExecutionOptions?.loopCount || 1"
    @move-item="moveComboItem"
    @remove-item="removeComboItem"
    @combine="openCombineDialog"
    @start="startCombo"
  />

  <!-- 组合动作 -> 合成为操作记录 命名对话框 -->
  <CombineNameDialog
    v-model="combineDialog"
    v-model:name="combineName"
    :combining="isCombining"
    @confirm="combineToRecord"
  />

  <!-- 操作反馈提示 -->
  <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000" location="top">
    {{ snackbar.text }}
  </v-snackbar>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import RecordDetailDialog from '@/components/operation-record/RecordDetailDialog.vue'
import RecordImportDialog from '@/components/operation-record/RecordImportDialog.vue'
import RecordComboDialog from '@/components/operation-record/RecordComboDialog.vue'
import CombineNameDialog from '@/components/operation-record/CombineNameDialog.vue'
import { useRecordList } from '@/composables/useRecordList.js'
import { useRecordImportExport } from '@/composables/useRecordImportExport.js'
import { useRecordCombo } from '@/composables/useRecordCombo.js'
import operationRecordAPI from '@/api/operationRecord.js'

const props = defineProps({
  refreshTrigger: {
    type: Number,
    required: true,
    description: '刷新触发器，每次值变化时重新加载数据'
  },
  isSuperAdmin: {
    type: Boolean,
    default: false
  },
  currentTenantId: {
    type: [String, Number],
    default: null
  }
})

const emit = defineEmits({
  'restore-config': (config) => {
    if (!config || !config.id) {
      console.warn('无效的配置数据')
      return false
    }
    return true
  },
  'search-completed': (totalCount) => typeof totalCount === 'number',
  'refresh-completed': null,
  'run-combo': null,
  'tenant-changed': null
})

const headers = ref([
  { title: '操作名称', key: 'operationName', width: '150px' },
  { title: '表情', key: 'emoji', width: '100px' },
  { title: '版本号', key: 'version', width: '100px' },
  { title: '设备型号', key: 'servoVersion', width: '100px' },
  { title: '创建时间', key: 'createdAt', width: '160px' },
  { title: '操作', key: 'actions', width: '120px' }
])

const favoriteOptions = [
  { title: '已收藏', value: 1 },
  { title: '未收藏', value: 0 }
]
const itemsPerPageOptions = [5, 10, 20, 50]

const selectedIds = ref([]) // 表格中勾选的记录 id 列表
const comboExecutionOptions = ref(null)
const normalizeTenantId = (tenantId) => (
  tenantId === null || tenantId === undefined || tenantId === '' || Number(tenantId) <= 0
    ? null
    : String(tenantId)
)
const selectedTenantId = ref(
  props.isSuperAdmin ? normalizeTenantId(props.currentTenantId) : null
)
const tenantOptions = ref([])
const tenantLoading = ref(false)
let tenantsLoaded = false
let defaultTenantApplied = false
const snackbar = ref({ show: false, text: '', color: 'success' })
const showSnackbar = (text, color = 'success') => {
  snackbar.value = { show: true, text, color }
}

// 列表 / 搜索 / 分页 / 收藏 / 删除 / 详情
const {
  isLoading, error, page, itemsPerPage, totalRecord, pageCount, operationRecords,
  showAdvancedSearch, advancedSearch, resetAdvancedSearch,
  deleteDialog, deleteRecord, confirmDeleteRecord, cancelDeleteRecord,
  batchDeleteDialog, batchDeleteIds, batchDeleteRecords, confirmBatchDelete, cancelBatchDelete,
  detailDialog, operationDetail, isDetailLoading, detailError, fetchOperationDetail,
  fetchOperationRecords, toggleFavorite
} = useRecordList({ tenantId: selectedTenantId })

const loadTenantOptions = async () => {
  if (!props.isSuperAdmin || tenantsLoaded || tenantLoading.value) return
  tenantLoading.value = true
  try {
    const data = await operationRecordAPI.getTenants()
    const tenants = data?.data?.list || data?.data?.records || []
    tenantOptions.value = tenants.map(tenant => ({
      title: tenant.name,
      value: String(tenant.id)
    }))
    tenantsLoaded = true
  } catch (err) {
    showSnackbar(err.message || '租户列表加载失败', 'error')
  } finally {
    tenantLoading.value = false
  }
}

// 导入 / 导出
const {
  isExporting, isImporting,
  importFileInput, importDialog, importRecords, importSelectedIndexes,
  exportRecords, triggerImport, handleImportFileChange, confirmImport
} = useRecordImportExport({
  selectedIds,
  showSnackbar,
  onImported: fetchOperationRecords
})

// 组合动作 / 合成为记录
const {
  comboDialog, comboLoading, comboItems, comboUniform, comboGlobalInterval,
  combineName, isCombining, combineDialog,
  openComboDialog, moveComboItem, removeComboItem, startCombo,
  openCombineDialog, combineToRecord
} = useRecordCombo({
  selectedIds,
  showSnackbar,
  emitRunCombo: (payload) => emit('run-combo', {
    ...payload,
    executionOptions: comboExecutionOptions.value
  }),
  onCombined: fetchOperationRecords
})

const openStandardComboDialog = async () => {
  comboExecutionOptions.value = null
  await openComboDialog()
  return comboDialog.value
}

// 供“功能性测试”复用当前表格勾选项和既有组合动作编排弹窗。
const openComboForBatch = async (executionOptions) => {
  if (selectedIds.value.length < 2) {
    showSnackbar('请先在操作记录中至少勾选 2 条记录', 'warning')
    return false
  }
  comboExecutionOptions.value = executionOptions
  await openComboDialog()
  if (!comboDialog.value) comboExecutionOptions.value = null
  return comboDialog.value
}

watch(comboDialog, (isOpen) => {
  if (!isOpen) comboExecutionOptions.value = null
})

const searchRecords = () => {
  page.value = 1
  fetchOperationRecords().then(() => {
    emit('search-completed', totalRecord.value)
  })
}

const handleResetSearch = () => {
  resetAdvancedSearch()
  searchRecords()
}

// 批量删除：删除成功后清空选中项并提示
const handleConfirmBatchDelete = async () => {
  const count = batchDeleteIds.value.length
  await confirmBatchDelete()
  if (!error.value) {
    selectedIds.value = []
    showSnackbar(`已删除 ${count} 条操作记录`)
  }
}

// 还原配置：把整条记录交给父组件处理
const restoreConfig = (item) => {
  try {
    const itemData = JSON.parse(JSON.stringify(item))
    emit('restore-config', itemData)
  } catch (err) {
    error.value = '参数解析失败'
    console.error('参数解析错误:', err)
  }
}

// 监听刷新触发器变化
watch(() => props.refreshTrigger, () => {
  fetchOperationRecords()
})

watch(
  [() => props.isSuperAdmin, () => props.currentTenantId],
  ([isSuperAdmin, currentTenantId], [, previousTenantId] = []) => {
    if (!isSuperAdmin) {
      defaultTenantApplied = false
      selectedTenantId.value = null
      return
    }

    loadTenantOptions()

    const normalizedTenantId = normalizeTenantId(currentTenantId)
    const tenantChanged = String(currentTenantId ?? '') !== String(previousTenantId ?? '')
    if (normalizedTenantId && (!defaultTenantApplied || tenantChanged)) {
      selectedTenantId.value = normalizedTenantId
      defaultTenantApplied = true
    }
  },
  { immediate: true }
)

watch(selectedTenantId, (tenantId) => {
  emit('tenant-changed', tenantId)
  selectedIds.value = []
  if (page.value !== 1) {
    page.value = 1
  } else {
    fetchOperationRecords()
  }
})

onMounted(() => {
  fetchOperationRecords()
})

defineExpose({
  fetchOperationRecords,
  openComboForBatch
})
</script>
